import { siteMeta } from '../data/siteMeta';
import React, { useState } from 'react';

function HowItWorks() {
  const [isExpanded, setIsExpanded] = useState(false);

  const steps = [
    {
      icon: 'fa-filter',
      color: 'bg-purple-100 text-purple-600',
      title: 'Filter by Category',
      description: 'Use the category tabs to browse by law type: Federal, Consumer Privacy, Social Media, AADC, App Store, Age Verification, AI Chatbot, or International.'
    },
    {
      icon: 'fa-search',
      color: 'bg-blue-100 text-blue-600',
      title: 'Search Any Keyword',
      description: 'Type a state name, law name, or topic (e.g. "COPPA", "California", "age verification") into the search bar to instantly filter results.'
    },
    {
      icon: 'fa-circle-check',
      color: 'bg-green-100 text-green-600',
      title: 'Filter by Status',
      description: 'Use the status filter to see only Active laws, Pending (not yet effective), Challenged (in court), or Blocked (enjoined/struck down) laws.'
    },
    {
      icon: 'fa-table-columns',
      color: 'bg-indigo-100 text-indigo-600',
      title: 'Switch View Modes',
      description: 'Toggle between Cards (visual overview), Table (dense data), or Timeline (chronological) views depending on how you need to analyze the data.'
    },
    {
      icon: 'fa-hand-pointer',
      color: 'bg-orange-100 text-orange-600',
      title: 'Click for Full Details',
      description: 'Click any law card or table row to open a detailed modal with applicability thresholds, key requirements, enforcement notes, and links to compliance resources.'
    },
    {
      icon: 'fa-globe',
      color: 'bg-teal-100 text-teal-600',
      title: 'Explore International Laws',
      description: 'Click the International tab or scroll down in All Laws to see children\'s privacy regulations from Australia, UK, EU, France, Canada, India, Japan, South Korea, and New Zealand.'
    },
    {
      icon: 'fa-sort',
      color: 'bg-pink-100 text-pink-600',
      title: 'Sort & Group',
      description: 'Sort laws by state/country, effective date, status, or category. Use "Group by State" to cluster multiple laws from the same jurisdiction together.'
    },
    {
      icon: 'fa-robot',
      color: 'bg-yellow-100 text-yellow-600',
      title: 'New: AI Chatbot Laws',
      description: '2026 adds a brand-new category tracking AI companion chatbot safety laws for minors — the fastest-growing area of children\'s digital safety regulation.'
    }
  ];

  const categories = [
    { key: 'Federal', icon: 'fa-landmark', color: 'bg-red-100 text-red-700', desc: 'Take It Down Act, COPPA updates, KOSA' },
    { key: 'Consumer Privacy', icon: 'fa-shield-alt', color: 'bg-purple-100 text-purple-700', desc: '21 state privacy laws with children\'s provisions' },
    { key: 'Social Media', icon: 'fa-users', color: 'bg-blue-100 text-blue-700', desc: 'Platform-specific age & consent requirements' },
    { key: 'AADC', icon: 'fa-code', color: 'bg-indigo-100 text-indigo-700', desc: 'Age-Appropriate Design Code laws (5 states)' },
    { key: 'App Store', icon: 'fa-mobile-alt', color: 'bg-cyan-100 text-cyan-700', desc: 'App store age verification & parental consent' },
    { key: 'Age Verification', icon: 'fa-id-card', color: 'bg-green-100 text-green-700', desc: '22+ states require age checks for adult content' },
    { key: 'AI Chatbot', icon: 'fa-robot', color: 'bg-yellow-100 text-yellow-700', desc: 'New 2026: AI companion safety for minors' },
    { key: 'International', icon: 'fa-globe', color: 'bg-teal-100 text-teal-700', desc: '18 jurisdictions including UK, EU, Australia, Canada, Brazil, India' }
  ];

  return (
    <div id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
      <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="fas fa-circle-question text-purple-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">How to Use This Tracker</h2>
              <p className="text-sm text-gray-500 mt-0.5">Learn how to navigate 125+ children's privacy laws efficiently</p>
            </div>
          </div>
          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-xl text-purple-500 ml-4`}></i>
        </button>

        {isExpanded && (
          <div className="border-t border-gray-100 p-6">
            {/* Intro */}
            <p className="text-gray-600 mb-8 max-w-3xl">
              This tracker covers <strong>125+ children's privacy and online safety laws</strong> across US federal, state, and international jurisdictions — refreshed quarterly (latest: <strong>{siteMeta.updateLabel}</strong>, {siteMeta.updatedOn}). Scroll up to the <strong>What's Next watchlist</strong> for 2027 effective dates and pending rulings. Use the tools below to find exactly what applies to your platform, state, or compliance use case.
            </p>

            {/* 8-step how-to */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {steps.map((step, idx) => (
                <div key={idx} className="flex gap-3 p-4 bg-white/60 rounded-lg border border-slate-100">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${step.color}`}>
                    <i className={`fas ${step.icon} text-sm`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">{step.title}</div>
                    <div className="text-xs text-gray-600 leading-relaxed">{step.description}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Category quick reference */}
            <div className="mb-8">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="fas fa-tags text-purple-600"></i>
                Law Categories at a Glance
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {categories.map((cat, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 ${cat.color}`}>
                      <i className={`fas ${cat.icon} text-xs`}></i>
                    </span>
                    <div>
                      <div className="text-xs font-bold text-gray-800">{cat.key}</div>
                      <div className="text-xs text-gray-500 leading-tight mt-0.5">{cat.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status explainer */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <h3 className="text-sm font-bold text-purple-900 mb-3 flex items-center gap-2">
                <i className="fas fa-info-circle text-purple-600"></i>
                Understanding Law Statuses
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold">Active</span>
                  <span className="text-purple-800">Currently enforceable</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold">Pending</span>
                  <span className="text-purple-800">Future effective date</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-semibold">Challenged</span>
                  <span className="text-purple-800">Active but in court</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-semibold">Blocked</span>
                  <span className="text-purple-800">Enjoined or struck down</span>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <p className="text-xs text-gray-400 mt-4">
              <i className="fas fa-exclamation-circle mr-1"></i>
              This tracker is for informational purposes only and does not constitute legal advice. Always consult qualified legal counsel for compliance decisions. Law statuses may change due to court rulings or legislative updates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default HowItWorks;
